import { MAIL_ARCHIVE_TYPE, type ArchiveSearchResult, type ArchiveType, type SoftwareDto } from '@bk/contracts';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { dateTime, humanize } from '../../lib/format';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, Empty, ErrorText, PageTitle } from '../../ui';

const PAGE = 50;
/** Colonne mostrate in tabella: le altre si vedono nel dettaglio della riga. */
const COLUMNS = 6;

type Row = ArchiveSearchResult['righe'][number];

/** Da dove arriva un tipo: il master del registro dei tipi, con il nome del programma se è un collegamento. */
function origin(type: ArchiveType, software: SoftwareDto[]): string {
  if (type.master === 'modello') return 'Ricavati dal sistema';
  if (type.master === 'archive') return 'File caricati';
  if (type.nome === MAIL_ARCHIVE_TYPE) return 'Posta';
  const s = software.find((x) => x.key === type.master);
  return `Programma: ${s?.product ?? s?.name ?? humanize(type.master)}`;
}

const show = (v: unknown): string => (v === null || v === undefined || v === '' ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v));

function RowDetail({ row, type, onClose }: { row: Row; type: ArchiveType; onClose: () => void }) {
  const labels = new Map(type.attributi.map((a) => [a.nome, a.etichetta ?? humanize(a.nome)]));
  // Prima gli attributi del tipo nel loro ordine, poi quelli arrivati in più dalla fonte.
  const keys = [...type.attributi.map((a) => a.nome), ...Object.keys(row.attributi).filter((k) => !labels.has(k))];
  return (
    <Card>
      <div className="mb-3 flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">Dettaglio</h3>
          <p className="text-xs text-ink/50">
            {row.riferimento ? `Riferimento ${row.riferimento} · ` : ''}
            {row.fonte} · {row.modalita} · acquisito {dateTime(row.acquisito_il)} · versione {row.versione}
          </p>
          {row.rimosso_il && <p className="mt-1 text-xs text-red-700">Rimosso dalla fonte il {dateTime(row.rimosso_il)}</p>}
        </div>
        <Button variant="ghost" onClick={onClose}>
          Chiudi
        </Button>
      </div>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[12rem_minmax(0,1fr)]">
        {keys.map((k) => (
          <div key={k} className="contents">
            <dt className="text-ink/55">{labels.get(k) ?? humanize(k)}</dt>
            <dd className="whitespace-pre-wrap break-words">{show(row.attributi[k])}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function TypeRows({ type }: { type: ArchiveType }) {
  const { tenant, t } = useTenant();
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [removed, setRemoved] = useState(false);
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const result = useQuery({
    queryKey: ['tenant', tenant.id, 'archive', 'search', type.nome, query, removed, offset],
    queryFn: () => api<ArchiveSearchResult>(t('/archive/search'), { body: { tipo: type.nome, testo: query || null, rimossi: removed, limite: PAGE, offset } }),
    placeholderData: (prev) => prev,
  });
  const data = result.data;
  const columns = type.attributi.slice(0, COLUMNS);
  const selected = data?.righe.find((r) => r.id === open);
  return (
    <div className="space-y-4">
      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold">{type.etichetta}</h2>
            <p className="text-xs text-ink/50">
              {type.entita} righe{type.rimossi ? ` · ${type.rimossi} rimosse` : ''} · {type.attributi.length} attributi
              {type.aggiornato_il ? ` · ultimo aggiornamento ${dateTime(type.aggiornato_il)}` : ''}
            </p>
          </div>
          <label className="flex items-center gap-1.5 text-xs text-ink/60">
            <input
              type="checkbox"
              checked={removed}
              onChange={(e) => {
                setRemoved(e.target.checked);
                setOffset(0);
              }}
            />
            Anche le rimosse
          </label>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setQuery(text.trim());
              setOffset(0);
            }}
          >
            <input className="w-48 rounded-lg border border-black/15 px-3 py-1.5 text-sm" placeholder="Cerca in tutti i campi…" value={text} onChange={(e) => setText(e.target.value)} />
            <Button variant="ghost" type="submit">
              Cerca
            </Button>
          </form>
        </div>
        <ErrorText>{result.error ? errorMessage(result.error) : ''}</ErrorText>
        <div className="overflow-x-auto">
          <table className="bk-list">
            <thead>
              <tr>
                {columns.map((a) => (
                  <th key={a.nome}>
                    {a.etichetta ?? humanize(a.nome)}
                  </th>
                ))}
                <th>Aggiornato</th>
              </tr>
            </thead>
            <tbody>
              {data?.righe.map((r) => (
                <tr
                  key={r.id}
                  className={`cursor-pointer ${open === r.id ? 'font-medium text-accent' : ''} ${r.rimosso_il ? 'text-ink/40 line-through' : ''}`}
                  onClick={() => setOpen(open === r.id ? null : r.id)}
                >
                  {columns.map((a) => (
                    <td key={a.nome} className="max-w-[16rem] truncate" title={show(r.attributi[a.nome])}>
                      {show(r.attributi[a.nome])}
                    </td>
                  ))}
                  <td className="whitespace-nowrap text-xs text-ink/45">
                    {dateTime(r.acquisito_il)}
                    {r.versione > 1 && ` · v${r.versione}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && !data.righe.length && <p className="py-4 text-center text-sm text-ink/50">Nessuna riga{query ? ' per questa ricerca' : ''}.</p>}
        </div>
        {data && data.totale > 0 && (
          <div className="mt-3 flex items-center gap-2 text-xs text-ink/55">
            <span className="flex-1">
              {offset + 1}–{offset + data.righe.length} di {data.totale}
              {type.attributi.length > COLUMNS ? ' · clicca una riga per vedere tutti i campi' : ''}
            </span>
            <Button variant="ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
              Precedenti
            </Button>
            <Button variant="ghost" disabled={offset + PAGE >= data.totale} onClick={() => setOffset(offset + PAGE)}>
              Successive
            </Button>
          </div>
        )}
      </Card>
      {selected && <RowDetail key={selected.id} row={selected} type={type} onClose={() => setOpen(null)} />}
    </div>
  );
}

/** Dati › Tutti i dati salvati: ciò che il sistema ha salvato nell'archivio dell'azienda, per tipo e provenienza. Sola lettura. */
export function StoredDataPage({ typeKey }: { typeKey?: string }) {
  const { tenant, isManager } = useTenant();
  const types = useTenantQuery<{ data: ArchiveType[] }>(['archive', 'types'], '/archive/types');
  const software = useTenantQuery<{ data: SoftwareDto[] }>(['software'], '/software');
  // La posta dell'azienda la leggono solo i responsabili.
  const list = (types.data?.data ?? []).filter((x) => isManager || x.nome !== MAIL_ARCHIVE_TYPE);
  const current = list.find((x) => x.nome === typeKey) ?? list[0];
  const groups = new Map<string, ArchiveType[]>();
  for (const x of list) {
    const g = origin(x, software.data?.data ?? []);
    groups.set(g, [...(groups.get(g) ?? []), x]);
  }
  return (
    <>
      <PageTitle title="Tutti i dati salvati" subtitle="Ciò che il sistema ha salvato nell’archivio dell’azienda: dai programmi collegati, dai file caricati, dalla posta e dal modello dell’azienda. Sola lettura: si corregge nella fonte." />
      <ErrorText>{types.error ? errorMessage(types.error) : ''}</ErrorText>
      {types.data && !list.length ? (
        <Empty>Nessun dato salvato ancora: arrivano collegando un programma o caricando un export (CSV o Excel) in Archivio.</Empty>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <nav className="space-y-4">
            {[...groups].map(([group, items]) => (
              <div key={group}>
                <p className="mb-1 px-2 text-xs font-medium uppercase tracking-wide text-ink/45">{group}</p>
                <ul>
                  {items.map((x) => (
                    <li key={x.nome}>
                      <button
                        className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-black/5 ${current?.nome === x.nome ? 'bg-black/5 font-medium' : ''}`}
                        onClick={() => navigate(`/t/${tenant.id}/dati/salvati/${x.nome}`)}
                      >
                        <span className="min-w-0 flex-1 truncate">{x.etichetta}</span>
                        <Badge>{x.entita}</Badge>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
          <div className="min-w-0">{current && <TypeRows key={current.nome} type={current} />}</div>
        </div>
      )}
    </>
  );
}
